import { path as d3Path } from "../d3.js";

export const CALCULATOR_ASSIGNMENT_CONNECTIONS_TAG =
    "fishy-calculator-assignment-connections";

const SVG_NAMESPACE = "http://www.w3.org/2000/svg";
const DEFAULT_RADIUS = 24;
const ACTION_EDGE_CLEARANCE = 18;
const CONNECTION_KEYS = Object.freeze(["lane", "source", "targets"]);
const TARGET_KEYS = Object.freeze([
    "boundaryElement",
    "closedElement",
    "connectionId",
    "disclosure",
    "element",
    "groupId",
    "itemId",
    "label",
]);

export class CalculatorAssignmentConnectionsContractError extends Error {
    constructor(message) {
        super(`calculator assignment connections: ${message}`);
        this.name = "CalculatorAssignmentConnectionsContractError";
    }
}

function contract(condition, message) {
    if (!condition) {
        throw new CalculatorAssignmentConnectionsContractError(message);
    }
}

function exactKeys(value, expected, label) {
    contract(value && typeof value === "object" && !Array.isArray(value), `${label} must be an object`);
    const actual = Object.keys(value).sort();
    contract(
        actual.length === expected.length
            && actual.every((key, index) => key === expected[index]),
        `${label} fields must be exactly: ${expected.join(", ")}`,
    );
}

function finiteNumber(value, label) {
    contract(typeof value === "number" && Number.isFinite(value), `${label} must be finite`);
    return value;
}

function point(value, label) {
    exactKeys(value, ["x", "y"], label);
    return Object.freeze({
        x: finiteNumber(value.x, `${label}.x`),
        y: finiteNumber(value.y, `${label}.y`),
    });
}

function nonEmptyString(value, label) {
    contract(typeof value === "string" && value.trim().length > 0, `${label} must be a non-empty string`);
    return value;
}

function pathString(draw) {
    const output = d3Path();
    draw(output);
    return output.toString();
}

function sourceDirection(sourceY, targets) {
    if (targets.every((target) => target.y > sourceY)) {
        return 1;
    }
    if (targets.every((target) => target.y < sourceY)) {
        return -1;
    }
    return 0;
}

function projectedHorizontalRadius(requestedRadius, source, laneX, targets) {
    const horizontalLimit = Math.min(
        (laneX - source.x) / 2,
        ...targets.map((target) => (target.actionBoundaryX - laneX) / 2),
    );
    return Math.max(0, Math.min(requestedRadius, horizontalLimit));
}

function projectedVerticalRadius(radius, sourceY, targetY) {
    const distance = Math.abs(targetY - sourceY);
    return distance > 0 ? Math.min(radius, distance / 2) : radius;
}

function drawSourcePath(source, laneX, radius, direction) {
    if (direction === 0 || radius === 0) {
        return Object.freeze({
            join: Object.freeze({ x: laneX, y: source.y }),
            path: pathString((path) => {
                path.moveTo(source.x, source.y);
                path.lineTo(laneX, source.y);
            }),
        });
    }
    const join = Object.freeze({
        x: laneX,
        y: source.y + (direction * radius),
    });
    return Object.freeze({
        join,
        path: pathString((path) => {
            path.moveTo(source.x, source.y);
            path.lineTo(laneX - radius, source.y);
            path.quadraticCurveTo(laneX, source.y, join.x, join.y);
        }),
    });
}

function drawTargetBranch(target, sourceY, laneX, radius) {
    const direction = target.y === sourceY ? 0 : target.y > sourceY ? 1 : -1;
    const join = Object.freeze({
        x: laneX,
        y: target.y - (direction * radius),
    });
    const horizontalStartX = laneX + radius;
    const path = pathString((output) => {
        output.moveTo(join.x, join.y);
        if (direction !== 0 && radius > 0) {
            output.quadraticCurveTo(laneX, target.y, horizontalStartX, target.y);
        }
        output.lineTo(target.x, target.y);
    });
    const unobstructedEndX = Math.max(
        horizontalStartX,
        target.actionBoundaryX - ACTION_EDGE_CLEARANCE,
    );
    return Object.freeze({
        connectionId: target.connectionId,
        itemId: target.itemId,
        groupId: target.groupId,
        label: target.label,
        target: Object.freeze({ x: target.x, y: target.y }),
        join,
        action: Object.freeze({
            x: horizontalStartX + ((unobstructedEndX - horizontalStartX) / 2),
            y: target.y,
        }),
        path,
    });
}

/**
 * Pure, presentation-only geometry. Every coordinate uses the workspace's
 * local coordinate system; callers subtract the workspace rectangle exactly
 * once from getBoundingClientRect() values.
 */
export function projectCalculatorAssignmentConnections(input) {
    const requiredInputKeys = ["height", "laneX", "source", "targets", "width"];
    const inputKeys = Object.keys(input ?? {}).sort();
    contract(
        inputKeys.length === requiredInputKeys.length
            || (
                inputKeys.length === requiredInputKeys.length + 1
                && inputKeys.includes("radius")
            ),
        `input fields must be exactly: ${requiredInputKeys.join(", ")}, with optional radius`,
    );
    contract(
        requiredInputKeys.every((key) => inputKeys.includes(key))
            && inputKeys.every((key) => requiredInputKeys.includes(key) || key === "radius"),
        `input fields must be exactly: ${requiredInputKeys.join(", ")}, with optional radius`,
    );
    const width = finiteNumber(input.width, "input.width");
    const height = finiteNumber(input.height, "input.height");
    const source = point(input.source, "input.source");
    const laneX = finiteNumber(input.laneX, "input.laneX");
    contract(width > 0 && height > 0, "input dimensions must be positive");
    contract(Array.isArray(input.targets) && input.targets.length > 0, "input.targets must not be empty");
    contract(laneX > source.x, "input.laneX must be to the right of input.source");

    const targets = input.targets.map((target, index) => {
        const requiredTargetKeys = ["connectionId", "groupId", "itemId", "label", "x", "y"];
        const targetKeys = Object.keys(target ?? {}).sort();
        contract(
            requiredTargetKeys.every((key) => targetKeys.includes(key))
                && targetKeys.every((key) => (
                    requiredTargetKeys.includes(key) || key === "actionBoundaryX"
                )),
            `input.targets[${index}] fields must be exactly: ${requiredTargetKeys.join(", ")}, with optional actionBoundaryX`,
        );
        const x = finiteNumber(target.x, `input.targets[${index}].x`);
        const projected = Object.freeze({
            connectionId: nonEmptyString(target.connectionId, `input.targets[${index}].connectionId`),
            itemId: target.itemId,
            groupId: nonEmptyString(target.groupId, `input.targets[${index}].groupId`),
            label: nonEmptyString(target.label, `input.targets[${index}].label`),
            x,
            y: finiteNumber(target.y, `input.targets[${index}].y`),
            actionBoundaryX: target.actionBoundaryX === undefined
                ? x
                : finiteNumber(
                    target.actionBoundaryX,
                    `input.targets[${index}].actionBoundaryX`,
                ),
        });
        contract(
            Number.isSafeInteger(projected.itemId) && projected.itemId > 0,
            `input.targets[${index}].itemId must be a positive safe integer`,
        );
        contract(projected.x > laneX, `input.targets[${index}].x must be to the right of input.laneX`);
        contract(
            projected.actionBoundaryX > laneX
                && projected.actionBoundaryX <= projected.x,
            `input.targets[${index}].actionBoundaryX must be right of input.laneX and no farther than x`,
        );
        return projected;
    });
    contract(
        new Set(targets.map((target) => target.connectionId)).size === targets.length,
        "input target connection IDs must be unique",
    );

    const requestedRadius = input.radius === undefined
        ? DEFAULT_RADIUS
        : finiteNumber(input.radius, "input.radius");
    contract(requestedRadius > 0, "input.radius must be positive");
    const radius = projectedHorizontalRadius(requestedRadius, source, laneX, targets);
    const sourcePathDirection = sourceDirection(source.y, targets);
    const sourceRadius = sourcePathDirection === 0
        ? radius
        : Math.min(
            radius,
            ...targets
                .map((target) => Math.abs(target.y - source.y))
                .filter((distance) => distance > 0)
                .map((distance) => distance / 2),
        );
    const sourceProjection = drawSourcePath(
        source,
        laneX,
        sourceRadius,
        sourcePathDirection,
    );
    const branches = Object.freeze(targets.map((target) => (
        drawTargetBranch(
            target,
            source.y,
            laneX,
            projectedVerticalRadius(radius, source.y, target.y),
        )
    )));
    const joins = [sourceProjection.join, ...branches.map((branch) => branch.join)];
    const trunk = Object.freeze({
        x: laneX,
        top: Math.min(...joins.map((join) => join.y)),
        bottom: Math.max(...joins.map((join) => join.y)),
    });
    const trunkPath = pathString((path) => {
        path.moveTo(trunk.x, trunk.top);
        path.lineTo(trunk.x, trunk.bottom);
    });
    const visiblePath = [
        sourceProjection.path,
        trunkPath,
        ...branches.map((branch) => branch.path),
    ].join("");

    return Object.freeze({
        width,
        height,
        visiblePath,
        trunk,
        source: Object.freeze({
            x: source.x,
            y: source.y,
            joinX: sourceProjection.join.x,
            joinY: sourceProjection.join.y,
            path: sourceProjection.path,
        }),
        branches,
    });
}

function elementContract(value, label) {
    contract(
        value && typeof value.getBoundingClientRect === "function",
        `${label} must be an element with getBoundingClientRect()`,
    );
    return value;
}

function disclosureContract(value, label) {
    if (value === null) {
        return null;
    }
    elementContract(value, label);
    contract(
        typeof value.open === "boolean",
        `${label} must be an open or closed details element`,
    );
    return value;
}

function parseConnections(value) {
    if (value === null) {
        return null;
    }
    exactKeys(value, CONNECTION_KEYS, "connections");
    const source = elementContract(value.source, "connections.source");
    const lane = elementContract(value.lane, "connections.lane");
    contract(Array.isArray(value.targets), "connections.targets must be an array");
    const targets = value.targets.map((target, index) => {
        exactKeys(target, TARGET_KEYS, `connections.targets[${index}]`);
        const itemId = target.itemId;
        contract(
            Number.isSafeInteger(itemId) && itemId > 0,
            `connections.targets[${index}].itemId must be a positive safe integer`,
        );
        const closedElement = target.closedElement === null
            ? null
            : elementContract(
                target.closedElement,
                `connections.targets[${index}].closedElement`,
            );
        const disclosure = disclosureContract(
            target.disclosure,
            `connections.targets[${index}].disclosure`,
        );
        contract(
            (closedElement === null) === (disclosure === null),
            `connections.targets[${index}] closedElement and disclosure must both be null or both be elements`,
        );
        return Object.freeze({
            boundaryElement: elementContract(
                target.boundaryElement,
                `connections.targets[${index}].boundaryElement`,
            ),
            closedElement,
            connectionId: nonEmptyString(
                target.connectionId,
                `connections.targets[${index}].connectionId`,
            ),
            disclosure,
            element: elementContract(target.element, `connections.targets[${index}].element`),
            groupId: nonEmptyString(target.groupId, `connections.targets[${index}].groupId`),
            itemId,
            label: nonEmptyString(target.label, `connections.targets[${index}].label`),
        });
    });
    contract(
        new Set(targets.map((target) => target.connectionId)).size === targets.length,
        "connections target IDs must be unique",
    );
    return Object.freeze({ source, lane, targets: Object.freeze(targets) });
}

function upgradeProperty(element, property) {
    if (!Object.prototype.hasOwnProperty.call(element, property)) {
        return;
    }
    const value = element[property];
    delete element[property];
    element[property] = value;
}

function svgElement(document, name) {
    return document.createElementNS(SVG_NAMESPACE, name);
}

function surfaceWithin(marker, label) {
    const surface = marker?.matches?.("[data-fishy-loot-row-item-surface]")
        ? marker
        : marker?.querySelector?.("[data-fishy-loot-row-item-surface]");
    contract(surface, `${label} must contain a loot row item surface`);
    return surface;
}

function absentDisclosureTarget(marker, label) {
    const disclosure = marker?.closest?.("[data-loot-editor-absent-disclosure]") ?? null;
    if (!disclosure) {
        return { closedElement: null, disclosure: null };
    }
    const closedElement = disclosure.querySelector?.(
        ":scope > [data-loot-editor-absent-summary]",
    );
    contract(closedElement, `${label} absent disclosure must contain its summary surface`);
    return { closedElement, disclosure };
}

const HTMLElementBase = globalThis.HTMLElement ?? class {};

export class FishyCalculatorAssignmentConnections extends HTMLElementBase {
    constructor() {
        super();
        this._connections = null;
        this._resizeObserver = null;
        this._animationFrame = null;
        this._visibleSvg = null;
        this._visiblePath = null;
        this._branchOverlays = new Map();
        this._handleGeometryChange = () => this._schedulePaint();
    }

    connectedCallback() {
        upgradeProperty(this, "connections");
        this.ownerDocument?.addEventListener?.("scroll", this._handleGeometryChange, {
            capture: true,
            passive: true,
        });
        this.ownerDocument?.defaultView?.addEventListener?.(
            "resize",
            this._handleGeometryChange,
            { passive: true },
        );
        this.ownerDocument?.defaultView?.addEventListener?.(
            "scroll",
            this._handleGeometryChange,
            { passive: true },
        );
        this.addEventListener?.("toggle", this._handleGeometryChange, true);
        this._observeEndpoints();
        this._schedulePaint();
    }

    disconnectedCallback() {
        this.ownerDocument?.removeEventListener?.("scroll", this._handleGeometryChange, true);
        this.ownerDocument?.defaultView?.removeEventListener?.(
            "resize",
            this._handleGeometryChange,
        );
        this.ownerDocument?.defaultView?.removeEventListener?.(
            "scroll",
            this._handleGeometryChange,
        );
        this.removeEventListener?.("toggle", this._handleGeometryChange, true);
        this._resizeObserver?.disconnect?.();
        this._resizeObserver = null;
        if (this._animationFrame !== null) {
            this.ownerDocument?.defaultView?.cancelAnimationFrame?.(this._animationFrame);
            this._animationFrame = null;
        }
    }

    get connections() {
        return this._connections;
    }

    set connections(value) {
        this._connections = parseConnections(value);
        this._observeEndpoints();
        this._reconcileBranchOverlays();
        this._schedulePaint();
    }

    _observeEndpoints() {
        this._resizeObserver?.disconnect?.();
        this._resizeObserver = null;
        const ResizeObserverOwner = this.ownerDocument?.defaultView?.ResizeObserver
            ?? globalThis.ResizeObserver;
        if (!this.isConnected || typeof ResizeObserverOwner !== "function") {
            return;
        }
        this._resizeObserver = new ResizeObserverOwner(this._handleGeometryChange);
        this._resizeObserver.observe(this);
        if (this._connections) {
            this._resizeObserver.observe(this._connections.source);
            this._resizeObserver.observe(this._connections.lane);
            for (const target of this._connections.targets) {
                this._resizeObserver.observe(target.element);
                if (target.closedElement) {
                    this._resizeObserver.observe(target.closedElement);
                }
            }
        }
    }

    _schedulePaint() {
        if (!this.isConnected || this._animationFrame !== null) {
            return;
        }
        const requestFrame = this.ownerDocument?.defaultView?.requestAnimationFrame;
        if (typeof requestFrame !== "function") {
            this._paint();
            return;
        }
        this._animationFrame = requestFrame.call(
            this.ownerDocument.defaultView,
            () => {
                this._animationFrame = null;
                this._paint();
            },
        );
    }

    _ensureVisibleSvg() {
        if (this._visibleSvg?.isConnected) {
            return;
        }
        const document = this.ownerDocument;
        if (!document?.createElementNS) {
            return;
        }
        const svg = svgElement(document, "svg");
        svg.setAttribute("aria-hidden", "true");
        svg.setAttribute("preserveAspectRatio", "none");
        svg.setAttribute("data-calculator-assignment-connections-svg", "");
        const path = svgElement(document, "path");
        path.setAttribute("data-calculator-assignment-connection-visible", "");
        svg.append(path);
        this.append(svg);
        this._visibleSvg = svg;
        this._visiblePath = path;
    }

    _createBranchOverlay(target) {
        const document = this.ownerDocument;
        const overlay = document.createElement("span");
        overlay.setAttribute("data-calculator-assignment-connection-branch", "");
        overlay.setAttribute("data-connection-id", target.connectionId);
        const svg = svgElement(document, "svg");
        svg.setAttribute("aria-hidden", "true");
        svg.setAttribute("preserveAspectRatio", "none");
        const hitPath = svgElement(document, "path");
        hitPath.setAttribute("data-calculator-assignment-connection-hit", "");
        svg.append(hitPath);
        const button = document.createElement("button");
        button.className = "btn btn-circle btn-error btn-sm";
        button.type = "button";
        button.setAttribute("data-calculator-assignment-connection-remove", "");
        button.setAttribute("data-action", "remove-item-from-group");
        button.setAttribute("data-item-id", String(target.itemId));
        button.setAttribute("data-group-id", target.groupId);
        button.setAttribute("aria-label", target.label);
        button.setAttribute("data-fishy-tooltip", target.label);
        button.setAttribute("data-fishy-tooltip-mode", "anchor-only");
        const icon = svgElement(document, "svg");
        icon.setAttribute("class", "size-4");
        icon.setAttribute("aria-hidden", "true");
        const use = svgElement(document, "use");
        use.setAttribute("href", "/img/icons.svg#fishy-delete-back-fill");
        icon.append(use);
        button.append(icon);
        overlay.append(svg, button);
        this.append(overlay);
        return Object.freeze({ overlay, svg, hitPath, button });
    }

    _reconcileBranchOverlays() {
        const targets = this._connections?.targets ?? [];
        const retained = new Set(targets.map((target) => target.connectionId));
        for (const [connectionId, entry] of this._branchOverlays) {
            if (!retained.has(connectionId)) {
                entry.overlay.remove?.();
                this._branchOverlays.delete(connectionId);
            }
        }
        if (!this.isConnected || !this.ownerDocument?.createElementNS) {
            return;
        }
        for (const target of targets) {
            let entry = this._branchOverlays.get(target.connectionId);
            if (!entry) {
                entry = this._createBranchOverlay(target);
                this._branchOverlays.set(target.connectionId, entry);
            }
            entry.button.setAttribute("data-item-id", String(target.itemId));
            entry.button.setAttribute("data-group-id", target.groupId);
            entry.button.setAttribute("aria-label", target.label);
            entry.button.setAttribute("data-fishy-tooltip", target.label);
        }
    }

    _clearPaint() {
        this._visiblePath?.removeAttribute?.("d");
        for (const entry of this._branchOverlays.values()) {
            entry.hitPath.removeAttribute?.("d");
            entry.button.style.removeProperty?.("left");
            entry.button.style.removeProperty?.("top");
        }
    }

    _paint() {
        if (!this._connections || this._connections.targets.length === 0) {
            this._clearPaint();
            return;
        }
        const { source, lane, targets } = this._connections;
        if (
            typeof lane.getClientRects === "function"
            && lane.getClientRects().length === 0
        ) {
            this._clearPaint();
            return;
        }
        const workspaceRect = this.getBoundingClientRect();
        const sourceRect = source.getBoundingClientRect();
        const laneRect = lane.getBoundingClientRect();
        const targetRects = targets.map((target) => {
            const closed = target.disclosure?.open === false;
            const element = closed ? target.closedElement : target.element;
            const boundaryElement = closed
                ? target.closedElement
                : target.boundaryElement;
            return {
                target,
                rect: element.getBoundingClientRect(),
                boundaryRect: boundaryElement.getBoundingClientRect(),
            };
        });
        const laneX = laneRect.left + (laneRect.width / 2) - workspaceRect.left;
        const sourcePoint = Object.freeze({
            x: sourceRect.right - workspaceRect.left,
            y: sourceRect.top + (sourceRect.height / 2) - workspaceRect.top,
        });
        const projectedTargets = targetRects.map(({ target, rect, boundaryRect }) => ({
            connectionId: target.connectionId,
            itemId: target.itemId,
            groupId: target.groupId,
            label: target.label,
            x: rect.left - workspaceRect.left,
            y: rect.top + (rect.height / 2) - workspaceRect.top,
            actionBoundaryX: boundaryRect.left - workspaceRect.left,
        }));
        if (
            workspaceRect.width <= 0
            || workspaceRect.height <= 0
            || laneX <= sourcePoint.x
            || projectedTargets.some((target) => target.x <= laneX)
        ) {
            this._clearPaint();
            return;
        }
        const projection = projectCalculatorAssignmentConnections({
            width: workspaceRect.width,
            height: workspaceRect.height,
            source: sourcePoint,
            laneX,
            targets: projectedTargets,
        });
        this._ensureVisibleSvg();
        this._reconcileBranchOverlays();
        const viewBox = `0 0 ${projection.width} ${projection.height}`;
        this._visibleSvg?.setAttribute?.("viewBox", viewBox);
        this._visiblePath?.setAttribute?.("d", projection.visiblePath);
        for (const branch of projection.branches) {
            const entry = this._branchOverlays.get(branch.connectionId);
            if (!entry) {
                continue;
            }
            entry.svg.setAttribute("viewBox", viewBox);
            entry.hitPath.setAttribute("d", branch.path);
            entry.button.style.left = `${branch.action.x}px`;
            entry.button.style.top = `${branch.action.y}px`;
        }
    }
}

export function registerCalculatorAssignmentConnections() {
    if (
        typeof globalThis.customElements === "undefined"
        || globalThis.customElements.get(CALCULATOR_ASSIGNMENT_CONNECTIONS_TAG)
    ) {
        return;
    }
    globalThis.customElements.define(
        CALCULATOR_ASSIGNMENT_CONNECTIONS_TAG,
        FishyCalculatorAssignmentConnections,
    );
}

function connectionHosts(root) {
    const hosts = [];
    if (root?.matches?.(CALCULATOR_ASSIGNMENT_CONNECTIONS_TAG)) {
        hosts.push(root);
    }
    hosts.push(...(root?.querySelectorAll?.(CALCULATOR_ASSIGNMENT_CONNECTIONS_TAG) ?? []));
    return hosts;
}

export function bindCalculatorAssignmentConnections(root) {
    for (const host of connectionHosts(root)) {
        const sourceMarkers = host.querySelectorAll?.("[data-loot-editor-selected-source]") ?? [];
        contract(sourceMarkers.length <= 1, "workspace must contain at most one selected source");
        const sourceMarker = sourceMarkers[0] ?? null;
        const lane = host.querySelector?.("[data-loot-editor-connection-lane]") ?? null;
        const targetMarkers = [
            ...(host.querySelectorAll?.("[data-loot-editor-connection-target]") ?? []),
        ];
        if (!sourceMarker || !lane || targetMarkers.length === 0) {
            host.connections = null;
            continue;
        }
        host.connections = {
            source: surfaceWithin(sourceMarker, "selected source"),
            lane,
            targets: targetMarkers.map((marker, index) => {
                const itemId = Number(marker.getAttribute?.("data-item-id"));
                const groupId = marker.getAttribute?.("data-group-id") ?? "";
                const label = marker.getAttribute?.("data-loot-editor-connection-label") ?? "";
                const disclosureTarget = absentDisclosureTarget(
                    marker,
                    `target ${index}`,
                );
                contract(
                    Number.isSafeInteger(itemId) && itemId > 0,
                    `target ${index} must have a positive data-item-id`,
                );
                nonEmptyString(groupId, `target ${index} data-group-id`);
                nonEmptyString(label, `target ${index} data-loot-editor-connection-label`);
                return {
                    boundaryElement: marker,
                    closedElement: disclosureTarget.closedElement,
                    connectionId: JSON.stringify([groupId, itemId]),
                    disclosure: disclosureTarget.disclosure,
                    element: surfaceWithin(marker, `target ${index}`),
                    groupId,
                    itemId,
                    label,
                };
            }),
        };
    }
}
